"use client"
import { Text } from "@/elements"
import { useSession } from "@/hooks/useSession"
import { HelpCircleIcon } from "@/icons/solid"
import clsx from "clsx"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"

/* 
https://developer.zendesk.com/api-reference/widget-messaging/web/core/
 */

// Utility to check if DOM is available
const canUseDOM = (): boolean => {
  return typeof window !== "undefined" && !!window.document && !!window.document.createElement
}

export const ZendeskAPI = (...args: any[]): void => {
  if (canUseDOM() && (window as any).zE) {
    ;(window as any).zE.apply(null, args)
  } else {
    console.warn("Zendesk is not initialized yet")
  }
}

export const Zendesk: React.FC = () => {
  const { data } = useSession()
  const path = usePathname()

  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  useEffect(() => {
    if (canUseDOM() && !(window as any).zE) {
      const script = document.createElement("script")
      script.id = "ze-snippet"
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${process.env.NEXT_PUBLIC_ZENDESK_KEY}`
      script.defer = true

      const handleScriptLoad = () => {
        setLoaded(true)
        ZendeskAPI("messenger:on", "open", function () {
          setHidden(true)
          setHasOpened(true)
        })
        ZendeskAPI("messenger:on", "close", function () {
          setHidden(false)
        })
        ZendeskAPI("messenger:on", "unreadMessages", function (count: number) {
          setUnreadMessageCount(count)
        })
      }

      script.addEventListener("load", handleScriptLoad)
      document.body.appendChild(script)

      return () => {
        if (canUseDOM()) {
          script.removeEventListener("load", handleScriptLoad)
          document.body.removeChild(script)

          delete (window as any).zE
          delete (window as any).zESettings
        }
      }
    }
  }, [])

  if (
    !data?.accessToken ||
    ![
      "/",
      "/hire",
      "/jobs",
      "/discover",
      "/messages",
      "/network",
      "/feed",
      "/jobs",
      "/settings/help-support",
      "/settings/account",
      "/settings/membership",
    ].some(p => path === p)
  )
    return null

  const extraPadding = path ? ["/jobs", "/hire"].includes(path) : false

  if (!loaded) return null

  return (
    <>
      <div
        onClick={() => ZendeskAPI("messenger", "open")}
        style={{
          transition: "all 300ms cubic-bezier(0.6, 0, 0.4, 1)",
        }}
        className={clsx(
          "fixed bottom-7 right-0 isolate z-40 hidden h-11 w-[91px] translate-x-[47px] cursor-pointer flex-row items-center gap-x-[10px] rounded-l-md border border-primary-2 bg-bg-1 p-[10px] hover:translate-x-[0px] sm:flex",
          hidden ? "opacity-0" : "opacity-100"
        )}>
        {unreadMessageCount > 0 ? (
          <div className="absolute -left-[6px] -top-[6px] flex size-[18px] items-center justify-center rounded-full bg-red-dark">
            <Text className="text-bg-1" variant="body3">
              {unreadMessageCount}
            </Text>
          </div>
        ) : null}
        <HelpCircleIcon className="fill-primary-4" />
        <Text className="text-primary-4" variant="CTASmall">
          Help
        </Text>
      </div>
      <div
        onClick={() => {
          ZendeskAPI("messenger", "open")
        }}
        style={{
          boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.10)",
          transition: "all 300ms cubic-bezier(0.6, 0, 0.4, 1)",
        }}
        className={clsx(
          "fixed right-5 isolate z-40 flex size-14 cursor-pointer flex-row items-center justify-center rounded-full bg-bg-1 sm:hidden",
          hasOpened ? "opacity-100" : "hidden opacity-0",
          extraPadding ? "bottom-24" : "bottom-5"
        )}>
        {unreadMessageCount > 0 ? (
          <div className="absolute -right-[3px] top-[1px] flex size-[18px] items-center justify-center rounded-full bg-red-dark">
            <Text className="text-bg-1" variant="body3">
              {unreadMessageCount}
            </Text>
          </div>
        ) : null}
        <HelpCircleIcon className="fill-primary-4" />
      </div>
    </>
  )
}

export default Zendesk
